import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl, assetUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import normalizeLanguageCode, { getLanguageShortLabel } from '@/utils/language.js';
import { getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import { sortDecksByPreferredLanguage } from '@/utils/deckSorting.js';

const normalizeDeck = (deck) => ({
  ...deck,
  id: Number(deck.id),
  name: deck.name ?? '',
  description: deck.description ?? deck.text ?? '',
  backgroundUrl: deck.backgroundUrl ?? deck.cardBackgroundUrl ?? '',
  cardBack: deck.cardBack || null,
  isSystem: Boolean(deck.isSystem ?? deck.system ?? deck.isDefault ?? deck.createdInAdmin),
  language: normalizeLanguageCode(deck.language),
  textColor: deck.textColor || '',
  renderMode: deck.renderMode === 'IMAGE' ? 'IMAGE' : 'TEXT',
  uploadedCardsCount: Number(deck.uploadedCardsCount || 0),
});

const MyCardsPage = () => {
  const { t, i18n } = useTranslation('dashboard');
  const { token, user } = useAuth();
  const location = useLocation();
  const { navigate } = useLanguageNavigation();
  const [preferredDeckId] = useState(() =>
    location.state?.selectDeckId ? Number(location.state.selectDeckId) : undefined,
  );

  useEffect(() => {
    if (location.state?.selectDeckId) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [decks, setDecks] = useState([]);
  const [currentDeckId, setCurrentDeckId] = useState(null);
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [deckCardMap, setDeckCardMap] = useState({});

  const [switchingDeck, setSwitchingDeck] = useState(false);
  const [switchMessage, setSwitchMessage] = useState('');
  const [switchError, setSwitchError] = useState('');

  const deckSliderRef = useRef(null);
  const cardTextRefs = useRef(new Map());
  const [showArrows, setShowArrows] = useState(false);

  const updateCardScrollbarCompensation = useCallback((node) => {
    if (!node) {
      return;
    }

    const scrollbarWidth = Math.max(0, node.offsetWidth - node.clientWidth);
    const hasOverflow = node.scrollHeight - node.clientHeight > 1;
    node.style.setProperty('--scrollbar-comp', hasOverflow ? `${scrollbarWidth}px` : '0px');
  }, []);

  const setCardTextRef = useCallback((deckId, node) => {
    if (node) {
      cardTextRefs.current.set(deckId, node);
      updateCardScrollbarCompensation(node);
      return;
    }
    cardTextRefs.current.delete(deckId);
  }, [updateCardScrollbarCompensation]);

  const conductorLanguage = normalizeLanguageCode(user?.uiLanguage || i18n.language);
  const sortedDecks = useMemo(
    () => sortDecksByPreferredLanguage(decks, conductorLanguage),
    [decks, conductorLanguage],
  );

  const updateCarouselArrows = useCallback(() => {
    const node = deckSliderRef.current;
    if (!node) {
      setShowArrows(false);
      return;
    }
    setShowArrows(node.scrollWidth - node.clientWidth > 4);
  }, []);

  const fetchDecks = useCallback(
    async (preferredId) => {
      if (!token) {
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await fetch(apiUrl('/api/decks/my'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || t('decks.states.error'));
        }
        const data = await response.json();
        const list = Array.isArray(data.decks) ? data.decks : [];
        const normalized = list.map(normalizeDeck);
        setDecks(normalized);
        const currentId = data.currentDeckId != null ? Number(data.currentDeckId) : null;
        setCurrentDeckId(currentId);
        const fallbackId = preferredId ?? currentId ?? (normalized.length ? normalized[0].id : null);
        const normalizedFallback = fallbackId != null ? Number(fallbackId) : null;
        setSelectedDeckId(normalizedFallback);
      } catch (fetchError) {
        setError(fetchError.message);
        setDecks([]);
        setCurrentDeckId(null);
        setSelectedDeckId(null);
      } finally {
        setLoading(false);
        updateCarouselArrows();
      }
    },
    [t, token, updateCarouselArrows],
  );

  useEffect(() => {
    if (!token || !decks.length) {
      return;
    }
    const missingDecks = decks.filter((deck) => deckCardMap[deck.id] === undefined);
    if (!missingDecks.length) {
      return;
    }

    const fetchCardsPreview = async () => {
      const updates = {};
      for (const deck of missingDecks) {
        try {
          const response = await fetch(apiUrl(`/api/decks/${deck.id}/cards`), {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (!response.ok) {
            updates[deck.id] = null;
            continue;
          }
          const data = await response.json();
          const list = Array.isArray(data.cards) ? data.cards : data;
          const firstCard = list.find((card) => card.cellNumber === 1) || list[0] || null;
          updates[deck.id] = firstCard
            ? {
                cellNumber: Number(firstCard.cellNumber) || 1,
                title: firstCard.title ?? '',
                text: firstCard.text ?? '',
                imageUrl: firstCard.imageUrl || null,
              }
            : null;
        } catch (cardsError) {
          console.error(cardsError);
          updates[deck.id] = null;
        }
      }
      if (Object.keys(updates).length) {
        setDeckCardMap((prev) => ({ ...prev, ...updates }));
      }
    };

    fetchCardsPreview();
  }, [decks, deckCardMap, token]);

  useEffect(() => {
    fetchDecks(preferredDeckId);
  }, [fetchDecks, preferredDeckId]);

  useEffect(() => {
    updateCarouselArrows();
  }, [sortedDecks, selectedDeckId, updateCarouselArrows]);

  useEffect(() => {
    const handleResize = () => updateCarouselArrows();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCarouselArrows]);

  useEffect(() => {
    const nodes = Array.from(cardTextRefs.current.values());
    if (!nodes.length) {
      return undefined;
    }

    const updateAllCardCompensation = () => {
      cardTextRefs.current.forEach((node) => updateCardScrollbarCompensation(node));
    };

    updateAllCardCompensation();
    window.addEventListener('resize', updateAllCardCompensation);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
        entries.forEach(({ target }) => updateCardScrollbarCompensation(target));
      })
      : null;
    nodes.forEach((node) => resizeObserver?.observe(node));

    const mutationObserver = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(() => updateAllCardCompensation())
      : null;
    nodes.forEach((node) => {
      mutationObserver?.observe(node, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });

    return () => {
      window.removeEventListener('resize', updateAllCardCompensation);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [decks, deckCardMap, updateCardScrollbarCompensation]);

  const handleDeckSelectChange = async (event) => {
    const newDeckId = Number(event);
    setSelectedDeckId(newDeckId);
    setSwitchError('');
    setSwitchMessage('');
    if (!token) {
      return;
    }
    try {
      setSwitchingDeck(true);
      const response = await fetch(apiUrl('/api/decks/current'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ deckId: newDeckId }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('decks.messages.switchError'));
      }
      setCurrentDeckId(newDeckId);
      setSwitchMessage(t('decks.messages.switchSuccess'));
    } catch (switchErr) {
      setSwitchError(switchErr.message);
    } finally {
      setSwitchingDeck(false);
    }
  };

  if (loading) {
    return (
      <div className="page-card">
        <p className="page-description">{t('decks.states.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-card">
        <h1>{t('decks.heading.title')}</h1>
        <p className="form-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="page-card">
      <div className="page-card-header deck-header">
        <div>
          <p className="page-caption">{t('decks.heading.caption')}</p>
          <h1>{t('decks.heading.title')}</h1>
          <p className="page-description">{t('decks.heading.description')}</p>
        </div>
        <div className="deck-header-actions">
          <button
            type="button"
            className="primary-button outlined"
            onClick={() => navigate('/dashboard/decks/create')}
          >
            {t('decks.actions.create')}
          </button>
        </div>
      </div>

      {decks.length === 0 ? (
        <p className="form-error">{t('decks.states.empty')}</p>
      ) : (
        <>
          <div className="deck-carousel-wrapper">
            {showArrows ? (
              <button
                type="button"
                className="carousel-arrow left"
                onClick={() => deckSliderRef.current?.scrollBy({ left: -320, behavior: 'smooth' })}
              >
                ◀
              </button>
            ) : null}
            <div className="deck-carousel" ref={deckSliderRef}>
              {sortedDecks.map((deck, index) => {
                const selected = deck.id === selectedDeckId;
                const isCurrent = deck.id === currentDeckId;
                const rawCardBackUrl = deck.cardBack?.imageUrl || deck.backgroundUrl || '';
                const resolvedCardBackUrl = rawCardBackUrl ? assetUrl(rawCardBackUrl) : '';
                const cardBackStyle = resolvedCardBackUrl
                  ? { backgroundImage: `url(${resolvedCardBackUrl})` }
                  : {};
                const previewCard = deckCardMap[deck.id];
                const imageDeck = deck.renderMode === 'IMAGE';
                const readyToUse = !imageDeck || deck.uploadedCardsCount === 72;
                const languageShort = getLanguageShortLabel(deck.language);
                const cardTitle = previewCard?.title || t('decks.states.cardTitle');
                const cardText = previewCard?.text || t('decks.states.noText');
                const cardNumber = previewCard?.cellNumber || 1;
                const deckName = deck.name || t('decks.states.deckNameFallback', { index: index + 1 });
                return (
                  <div key={deck.id} className={`deck-card-preview ${selected ? 'selected' : ''}`}>
                    <div className={`deck-card-preview__image${imageDeck ? ' is-image-deck' : ''}`} style={imageDeck ? {} : cardBackStyle}>
                      <div className="deck-language-badge" aria-label={t('decks.card.language', { language: languageShort })}>
                        {languageShort}
                      </div>
                      {imageDeck ? (
                        previewCard?.imageUrl
                          ? <img className="deck-card-preview__artwork" src={assetUrl(previewCard.imageUrl)} alt={cardTitle} loading="lazy" />
                          : <p className="image-deck-editor__missing">{t('imageDeck.notReady', { count: deck.uploadedCardsCount })}</p>
                      ) : (
                        <div
                          className="deck-card-preview__card"
                          style={getDeckCardTextStyle(deck.textColor)}
                        >
                          <span className="deck-card-preview__cell">{cardNumber}</span>
                          <h3 className="deck-card-preview__card-title">{cardTitle}</h3>
                          <p
                            className="deck-card-preview__card-text"
                            ref={(node) => setCardTextRef(deck.id, node)}
                          >
                            {cardText}
                          </p>
                        </div>
                      )}
                      <button
                        type="button"
                        className="deck-card-eye"
                        onClick={() => navigate(`/dashboard/my-cards/${deck.id}`, { state: { selectDeckId: deck.id } })}
                        title={t('decks.actions.open')}
                      >
                        👁
                      </button>
                    </div>
                    <div className="deck-card-preview__actions">
                      <div className="deck-card-preview__info">
                        <h3 className="deck-name">{deckName}</h3>
                        <span className={`deck-type-badge ${deck.isSystem ? 'system' : 'custom'}`}>
                          {deck.isSystem ? t('decks.badges.system') : t('decks.badges.custom')}
                        </span>
                        {imageDeck && <span className="form-hint">{t(readyToUse ? 'imageDeck.ready' : 'imageDeck.notReady', { count: deck.uploadedCardsCount })}</span>}
                      </div>
                      <div className="deck-card-actions">
                        <button
                          type="button"
                          className={selected ? 'primary-button outlined success' : 'primary-button'}
                          disabled={switchingDeck || isCurrent || !readyToUse}
                          onClick={() => handleDeckSelectChange(deck.id)}
                        >
                          {selected ? t('decks.buttons.selected') : t('decks.buttons.select')}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {showArrows ? (
              <button
                type="button"
                className="carousel-arrow right"
                onClick={() => deckSliderRef.current?.scrollBy({ left: 320, behavior: 'smooth' })}
              >
                ▶
              </button>
            ) : null}
          </div>
          <div className="deck-selector-status">
            {switchMessage && <p className="form-success">{switchMessage}</p>}
            {switchError && <p className="form-error">{switchError}</p>}
          </div>
        </>
      )}
    </div>
  );
};

export default MyCardsPage;
